// ---------------------------------------------------------------------------
// ConfirmActionDialog — confirmation for approve / hold / decline / cancel
//
// Extends the common ConfirmDialog to support an optional reason field
// and loading state.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { Modal } from '@/components/common';
import { Button } from '@/components/common';

export interface ConfirmActionDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
  isDangerous?: boolean;
  isLoading?: boolean;
  showReason?: boolean;
  reasonRequired?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  reasonMaxLength?: number;
}

export function ConfirmActionDialog({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isDangerous = false,
  isLoading = false,
  showReason = false,
  reasonRequired = false,
  reasonLabel = 'Reason',
  reasonPlaceholder = 'Enter reason...',
  reasonMaxLength = 500,
}: ConfirmActionDialogProps) {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (showReason && reasonRequired && !trimmed) {
      setReasonError('Reason is required');
      return;
    }
    onConfirm(showReason ? trimmed || undefined : undefined);
    setReason('');
    setReasonError('');
  };

  const handleCancel = () => {
    setReason('');
    setReasonError('');
    onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      onClose={handleCancel}
      className="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            {cancelText}
          </Button>
          <Button
            variant={isDangerous ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing\u2026' : confirmText}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-[#666]">{message}</p>

        {showReason && (
          <div>
            <label
              htmlFor="confirm-reason"
              className="mb-1 block text-[12px] font-medium text-[#666]"
            >
              {reasonLabel}
              {reasonRequired && <span className="text-[#c62828]"> *</span>}
            </label>
            <textarea
              id="confirm-reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError('');
              }}
              maxLength={reasonMaxLength}
              rows={3}
              className="w-full resize-none rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
              placeholder={reasonPlaceholder}
            />
            <div className="mt-1 flex justify-between">
              {reasonError ? <p className="text-xs text-[#c62828]">{reasonError}</p> : <span />}
              <span className="text-[11px] text-[#999]">
                {reason.length}/{reasonMaxLength}
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
